import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  total_webhooks: number
  active_webhooks: number
  deliveries_today: number
  deliveries_mtd: number
  success_rate: number
  avg_response_time_ms: number
}

interface Webhook {
  id: string
  name: string
  url: string
  events: string[]
  deliveries_today: number
  success_rate: number
  status: string
}

interface Delivery {
  id: string
  webhook: string
  event: string
  timestamp: string
  response_time: string
  status: string
}

export default function WebhookMgmtPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [webhooks, setWebhooks] = useState<Webhook[]>([])
  const [deliveries, setDeliveries] = useState<Delivery[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, webhooksRes, deliveriesRes] = await Promise.all([
          fetch('/api/webhookmgmt/statistics'),
          fetch('/api/webhookmgmt/webhooks'),
          fetch('/api/webhookmgmt/deliveries')
        ])
        setStatistics(await statsRes.json())
        setWebhooks(await webhooksRes.json())
        setDeliveries(await deliveriesRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      'Active': 'bg-green-100 text-green-800',
      'Paused': 'bg-yellow-100 text-yellow-800',
      'Success': 'bg-green-100 text-green-800',
      'Failed': 'bg-red-100 text-red-800',
      'Pending': 'bg-blue-100 text-blue-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading Webhook Management...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Webhook Management</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{statistics.total_webhooks}</div>
              <div className="text-sm text-gray-500">Total Webhooks</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{statistics.active_webhooks}</div>
              <div className="text-sm text-gray-500">Active</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">{statistics.deliveries_today.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Deliveries Today</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-indigo-600">{(statistics.deliveries_mtd / 1000).toFixed(0)}K</div>
              <div className="text-sm text-gray-500">Deliveries MTD</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-orange-600">{statistics.success_rate}%</div>
              <div className="text-sm text-gray-500">Success Rate</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-teal-600">{statistics.avg_response_time_ms}ms</div>
              <div className="text-sm text-gray-500">Avg Response</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Configured Webhooks</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Events</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Success</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {webhooks.map((wh) => (
                    <tr key={wh.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{wh.name}</td>
                      <td className="px-4 py-3 text-gray-600">{wh.events.join(', ')}</td>
                      <td className="px-4 py-3 text-gray-600">{wh.success_rate}%</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(wh.status)}`}>
                          {wh.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Recent Deliveries</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Webhook</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Event</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {deliveries.map((d) => (
                    <tr key={d.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{d.webhook}</td>
                      <td className="px-4 py-3 text-gray-600">{d.event}</td>
                      <td className="px-4 py-3 text-gray-600">{d.response_time}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(d.status)}`}>
                          {d.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
